import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, Tag, X } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { formatIdr } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * Alur pembayaran, dari keranjang sampai halaman bayar.
 *
 * Tiga langkah, dan urutannya penting: metode pembayaran yang tersedia
 * bergantung pada nominal, sedangkan nominal bergantung pada kode diskon.
 * Karena itu diskon diterapkan lebih dulu, baru metode ditanyakan.
 *
 * Tidak ada satu pun angka rupiah di layar ini yang dihitung di peramban.
 * Semuanya datang dari server, termasuk potongan diskonnya. Menghitung ulang
 * di sini akan menghasilkan selisih pembulatan yang membuat pembeli merasa
 * ditipu, dan lebih buruk lagi, membuka jalan menyetir nominal dari luar.
 */

const NAMA_KELOMPOK = {
  qris: 'QRIS',
  ewallet: 'Dompet digital',
  va: 'Transfer bank (Virtual Account)',
  kartu: 'Kartu kredit',
  gerai: 'Bayar di gerai',
  lainnya: 'Lainnya',
};

/** QRIS lebih dulu: paling murah biayanya dan paling luas jangkauannya. */
const URUTAN_KELOMPOK = ['qris', 'ewallet', 'va', 'gerai', 'kartu', 'lainnya'];

const BarisTotal = ({ label, nilai, tebal, hijau }) => (
  <div className="flex items-center justify-between py-1 text-sm">
    <span className={cn(tebal ? 'font-bold text-[var(--text-primary)]' : 'text-[var(--text-muted)]')}>
      {label}
    </span>
    <span
      className={cn(
        'tabular',
        tebal && 'text-base font-bold text-[var(--text-primary)]',
        hijau && 'font-semibold text-success-600',
        !tebal && !hijau && 'text-[var(--text-secondary)]',
      )}
    >
      {nilai}
    </span>
  </div>
);

export const CheckoutDialog = ({ keranjang, judul, kodeAwal, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  // Kode yang dibawa dari halaman harga langsung dipasang, bukan sekadar
  // diisikan ke kotaknya. Orang yang mengklik tombol diskon di pengumuman
  // sudah memilih memakai kodenya; menyuruhnya menekan "pakai" sekali lagi
  // adalah langkah yang tidak menambah keputusan apa pun.
  const [kodeKetik, setKodeKetik] = useState(kodeAwal ?? '');
  const [kodeDipakai, setKodeDipakai] = useState(kodeAwal ?? '');
  const [metode, setMetode] = useState(null);

  /** Keranjang beserta kode yang sudah diterima server. */
  const permintaan = useMemo(
    () => ({ ...keranjang, ...(kodeDipakai ? { kodeDiskon: kodeDipakai } : {}) }),
    [keranjang, kodeDipakai],
  );

  const { data: dataMetode, isLoading: memuatMetode, isError, error } = useQuery({
    queryKey: ['payment-methods', permintaan],
    queryFn: () => api.post('/billing/payment-methods', permintaan),
    enabled: Boolean(keranjang),
    retry: false,
  });

  const rincian = dataMetode?.data;
  const metodeTersedia = rincian?.metode ?? [];

  // Metode yang dipilih dilepas kalau ia tidak ada lagi di daftar terbaru.
  // Daftar berubah saat nominal berubah, dan membiarkan pilihan lama
  // membuat pembayaran ditolak Duitku dengan pesan yang membingungkan.
  useEffect(() => {
    if (metode && !metodeTersedia.some((m) => m.kode === metode)) setMetode(null);
  }, [metode, metodeTersedia]);

  const pakaiKupon = useMutation({
    mutationFn: (kode) => api.post('/billing/coupon/check', { ...keranjang, kodeDiskon: kode }),
    onSuccess: (res, kode) => {
      setKodeDipakai(kode);
      toast.success('Kode diskon dipakai', {
        description: `Potongan ${formatIdr(res?.data?.diskon?.potonganIdr ?? 0)}.`,
      });
    },
    onError: (err) =>
      toast.error('Kode tidak bisa dipakai', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  const bayar = useMutation({
    mutationFn: () => api.post('/billing/checkout', { ...permintaan, paymentMethod: metode }),
    onSuccess: (res) => {
      const hasil = res?.data;
      queryClient.invalidateQueries({ queryKey: ['usage'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });

      if (hasil?.otomatis && hasil?.paymentUrl) {
        // Dibuka di tab baru, bukan menggantikan halaman ini. Pembeli yang
        // batal di halaman bayar masih menemukan Crawel terbuka di belakang,
        // bukan harus menavigasi kembali dari nol.
        window.open(hasil.paymentUrl, '_blank', 'noopener');
        toast.success('Halaman pembayaran dibuka', {
          description: `Nomor pesanan ${hasil.orderId}. Kuota bertambah otomatis setelah pembayaran diterima.`,
        });
      } else {
        toast.success('Tagihan dibuat', {
          description: `Nomor pesanan ${hasil.orderId}. Hubungi admin untuk menyelesaikan pembayaran.`,
        });
      }
      onClose();
    },
    onError: (err) =>
      toast.error('Pembayaran gagal dibuat', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  if (!keranjang) return null;

  const otomatis = rincian?.otomatis !== false;
  const perluMetode = otomatis && metodeTersedia.length > 0;
  // Halangan nominal mematikan tombolnya. Membiarkannya aktif berarti pembeli
  // menekannya lalu menerima penolakan dari server, dan penolakan yang datang
  // belakangan selalu terasa seperti kerusakan.
  const siapBayar = !rincian?.halangan && (!perluMetode || Boolean(metode));

  const kelompok = URUTAN_KELOMPOK.map((k) => ({
    kunci: k,
    nama: NAMA_KELOMPOK[k],
    items: metodeTersedia.filter((m) => m.kelompok === k),
  })).filter((g) => g.items.length > 0);

  return (
    <Modal
      open
      onClose={onClose}
      title={judul ?? 'Selesaikan pembayaran'}
      size="lg"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-[var(--text-muted)]">Total tagihan</p>
            <p className="text-lg font-bold text-[var(--text-primary)]">
              {rincian ? formatIdr(rincian.total) : '...'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Batal
            </Button>
            <Button
              icon={otomatis ? ExternalLink : undefined}
              loading={bayar.isPending}
              disabled={!rincian || !siapBayar}
              onClick={() => bayar.mutate()}
            >
              {otomatis ? 'Lanjut ke pembayaran' : 'Buat tagihan'}
            </Button>
          </div>
        </div>
      }
    >
      {isError ? (
        <p className="py-6 text-center text-sm text-danger-500">
          {error instanceof ApiError ? error.message : 'Rincian tagihan tidak bisa dimuat.'}
        </p>
      ) : memuatMetode && !rincian ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-[var(--text-muted)]">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Menyiapkan rincian tagihan...
        </div>
      ) : (
        <div className="space-y-5">
          <section>
            <h3 className="mb-2 text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
              Rincian
            </h3>
            <div className="rounded-xl border border-[var(--border-subtle)] px-4 py-3">
              {(rincian.items ?? []).map((i) => (
                <BarisTotal key={i.nama} label={i.nama} nilai={formatIdr(i.subtotalIdr)} />
              ))}
              <div className="mt-1 border-t border-[var(--border-subtle)] pt-2">
                <BarisTotal label="Subtotal" nilai={formatIdr(rincian.subtotalIdr)} />
                {rincian.diskon && (
                  <BarisTotal
                    label={`Diskon ${rincian.diskon.kode}`}
                    nilai={`- ${formatIdr(rincian.diskon.potonganIdr)}`}
                    hijau
                  />
                )}
                <BarisTotal label="Total" nilai={formatIdr(rincian.total)} tebal />
              </div>
            </div>
          </section>

          <section>
            <h3 className="mb-2 text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
              Kode diskon
            </h3>
            {kodeDipakai ? (
              <div className="flex items-center gap-2 rounded-lg border border-success-200 bg-success-50 px-3 py-2 dark:border-success-500/25 dark:bg-success-500/10">
                <Tag className="size-4 shrink-0 text-success-600" aria-hidden="true" />
                <span className="flex-1 text-sm font-semibold text-[var(--text-primary)]">
                  {kodeDipakai}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setKodeDipakai('');
                    setKodeKetik('');
                  }}
                  className="rounded p-1 text-[var(--text-muted)] hover:text-danger-600"
                  aria-label="Lepas kode diskon"
                >
                  <X className="size-4" />
                </button>
              </div>
            ) : (
              <form
                className="flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  const kode = kodeKetik.trim();
                  if (kode) pakaiKupon.mutate(kode);
                }}
              >
                <Input
                  value={kodeKetik}
                  onChange={(e) => setKodeKetik(e.target.value.toUpperCase())}
                  placeholder="Punya kode diskon?"
                  className="flex-1"
                  autoComplete="off"
                />
                <Button
                  type="submit"
                  variant="secondary"
                  loading={pakaiKupon.isPending}
                  disabled={!kodeKetik.trim()}
                >
                  Pakai
                </Button>
              </form>
            )}
          </section>

          {/* Halangan nominal dijelaskan, bukan dibiarkan jadi daftar kosong.

              Duitku tetap menawarkan seluruh metodenya untuk nominal sekecil
              apa pun lalu menolaknya saat bayar, jadi kitalah yang menahan.
              Tanpa penjelasan ini, layar cuma memperlihatkan bagian metode
              yang hilang, dan pembeli tidak punya cara menebak sebabnya. */}
          {rincian?.halangan && (
            <section
              role="alert"
              className="rounded-xl border border-warning-200 bg-warning-50 px-4 py-3.5 dark:border-warning-500/25 dark:bg-warning-500/10"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle
                  className="mt-0.5 size-[18px] shrink-0 text-warning-600 dark:text-warning-500"
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-warning-700 dark:text-warning-500">
                    Belum bisa dibayar otomatis
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-warning-700/90 dark:text-warning-500/90">
                    {rincian.halangan.pesan}
                  </p>
                  <p className="mt-1.5 text-xs leading-relaxed text-warning-700/80 dark:text-warning-500/80">
                    {rincian.halangan.saran}
                  </p>
                </div>
              </div>
            </section>
          )}

          {perluMetode && (
            <section>
              <h3 className="mb-2 text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
                Metode pembayaran
              </h3>
              <div className="space-y-4">
                {kelompok.map((g) => (
                  <div key={g.kunci}>
                    <p className="mb-1.5 flex items-center gap-2 text-xs font-semibold text-[var(--text-secondary)]">
                      {g.nama}
                      {g.kunci === 'qris' && (
                        <Badge tone="success" size="sm">
                          Paling praktis
                        </Badge>
                      )}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {g.items.map((m) => (
                        <button
                          key={m.kode}
                          type="button"
                          onClick={() => setMetode(m.kode)}
                          aria-pressed={metode === m.kode}
                          className={cn(
                            'flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                            metode === m.kode
                              ? 'border-accent-600 bg-accent-50 dark:bg-accent-500/10'
                              : 'border-[var(--border-subtle)] hover:border-[var(--border-default)]',
                          )}
                        >
                          {m.gambar ? (
                            <img src={m.gambar} alt="" className="h-6 w-10 shrink-0 object-contain" />
                          ) : (
                            <span className="grid h-6 w-10 shrink-0 place-items-center rounded bg-[var(--surface-sunken)] font-mono text-[10px] font-bold">
                              {m.kode}
                            </span>
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-[var(--text-primary)]">
                              {m.nama}
                            </span>
                            {m.biaya > 0 && (
                              <span className="block text-xs text-[var(--text-muted)]">
                                Biaya {formatIdr(m.biaya)}
                              </span>
                            )}
                          </span>
                          {metode === m.kode && (
                            <CheckCircle2 className="size-4 shrink-0 text-accent-600" aria-hidden="true" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {!otomatis && (
            <p className="rounded-lg bg-[var(--surface-sunken)] px-4 py-3 text-sm text-[var(--text-secondary)]">
              Pembayaran otomatis sedang tidak aktif. Tagihan tetap dibuat, dan admin akan
              mengaktifkan paketnya setelah pembayaran diterima.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
};
